import { useMemo } from 'react';
import {
  PlusIcon,
  EyeIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import Card, { CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  TableContainer, Table, TableHead, TableBody, TableRow, TableHeaderCell, TableCell,
} from '@/components/ui/Table';
import { useApi } from '@/hooks/useApi';

interface Feed {
  id: number;
  name: string;
  book_count: number;
  created_at: string;
  published_at: string | null;
  status: string;
}

interface FeedsListResponse {
  items: Feed[];
  total: number;
  page: number;
  pageSize: number;
}

export default function DistFeedsPage() {
  // Build API endpoint
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    params.set('pageSize', '50');
    return `distribution/feeds?${params.toString()}`;
  }, []);

  // Fetch feeds from API
  const { data, isLoading, error, refetch } = useApi<FeedsListResponse>(apiEndpoint);

  const feeds = data?.items || [];

  // Calculate stats from real data
  const stats = useMemo(() => ({
    total: feeds.length,
    published: feeds.filter(f => f.status === 'published').length,
    drafts: feeds.filter(f => f.status === 'draft').length,
  }), [feeds]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">ONIX Feeds</h1>
          <p className="text-slate-500 mt-1">Manage and publish ONIX metadata feeds</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => refetch()}
            disabled
          >
            Refresh
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" />} disabled>
            Create Feed
          </Button>
        </div>
      </div>

      {/* Coming Soon Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 p-6 text-white shadow-lg">
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full bg-white/10" />
        <div className="relative flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
            <WrenchScrewdriverIcon className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Coming Soon</h3>
            <p className="text-sm text-white/80">
              ONIX Feed management is currently under development. Stay tuned for updates!
            </p>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Failed to load feeds</h3>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />} disabled>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-500 mt-4">Loading feeds...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && feeds.length === 0 && (
        <EmptyState
          icon={<DocumentTextIcon className="w-12 h-12" />}
          title="No feeds found"
          description="Get started by creating your first ONIX feed"
          action={
            <Button leftIcon={<PlusIcon className="w-4 h-4" />} disabled>
              Create Feed
            </Button>
          }
        />
      )}

      {/* Stats and Table - only show when there are feeds */}
      {!error && feeds.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardBody className="text-center">
              <p className="text-3xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-sm text-slate-500">Total Feeds</p>
            </CardBody></Card>
            <Card><CardBody className="text-center">
              <p className="text-3xl font-bold text-success-500">{stats.published}</p>
              <p className="text-sm text-slate-500">Published</p>
            </CardBody></Card>
            <Card><CardBody className="text-center">
              <p className="text-3xl font-bold text-warning-500">{stats.drafts}</p>
              <p className="text-sm text-slate-500">Drafts</p>
            </CardBody></Card>
          </div>

          <Card padding="none">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow isHoverable={false}>
                    <TableHeaderCell>Feed Name</TableHeaderCell>
                    <TableHeaderCell>Books</TableHeaderCell>
                    <TableHeaderCell>Created</TableHeaderCell>
                    <TableHeaderCell>Published</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feeds.map((feed) => (
                    <TableRow key={feed.id}>
                      <TableCell className="font-medium">{feed.name}</TableCell>
                      <TableCell>{feed.book_count}</TableCell>
                      <TableCell>{feed.created_at}</TableCell>
                      <TableCell>{feed.published_at || '--'}</TableCell>
                      <TableCell>
                        <Badge variant={feed.status === 'published' ? 'success' : 'warning'}>{feed.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" leftIcon={<EyeIcon className="w-3 h-3" />} disabled>
                            Preview
                          </Button>
                          <Button variant="ghost" size="sm" leftIcon={<CheckCircleIcon className="w-3 h-3" />} disabled>
                            Validate
                          </Button>
                          <Button variant="ghost" size="sm" leftIcon={<ArrowUpTrayIcon className="w-3 h-3" />} disabled>
                            Publish
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}
    </div>
  );
}

